import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";

import { userid } from "../../../Common/Credentials.js";
import { spin } from "../../../Common/NewLoader.jsx";
import { appmasterid } from "../../../Common/Permissions.js";
import Header from "../../../Component/Header.js";
import Menu from "../../../Component/Menu.js";
import Footer from "../../../Component/Footer.js";
import CategoryServices from "../CategoryServices.js";

import DeleteIcon from "../../../images/Icons/delete.png";
import Breadcrumb from "../../../NewUI/NewComponents/Breadcrumb.jsx";

const Categorylvl3 = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'category', direction: 'asc' });

  const [mcategory, setMcategory] = useState(location.state?.mcategory || '');
  const [mcatid, setMcatid] = useState(location.state?.mcatid || '');
  const [category2, setCategory2] = useState(location.state?.categorylvl2?.name || '');
  const [category2id, setCategory2id] = useState(location.state?.categorylvl2?.id || location.state?.categorylvl2 || '');
  const [categoryType, setCategoryType] = useState(location.state?.categoryType || '');

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshCategories = useCallback(() => {
    if (!category2id) return;

    setLoading(true);
    CategoryServices.getCategorylvl2ById(category2id)
      .then((response) => {
        const categoriesData = response.data.category || [];
        const sortedData = categoriesData.sort((a, b) => a.category?.localeCompare(b.category));
        setCategories(sortedData);
        setFilteredData(sortedData);
        setCategory2(response.data.name);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [category2id, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    if (!location.state) {
      navigate("/category");
      return;
    }

    refreshCategories();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshCategories, location.state]);

  useEffect(() => {
    updateFilteredData();
  }, [categories, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = categories;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = categories.filter(category =>
        category.category?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const deleteCategory = (id, categoryName) => {
    Swal.fire({
      title: `Delete ${categoryName}?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        CategoryServices.deleteCategory(id)
          .then(() => {
            Swal.fire({
              title: "Deleted!",
              text: "Category has been deleted.",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              setLoading(true);
              refreshCategories();
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: "Error occurred while deleting",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              });
            }
          });
      }
    });
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} categories
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No categories found matching your search' : 'No categories available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`} style={{ tableLayout: 'fixed' }}>
            <thead className="UI-table-header">
              <tr>
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''}`} style={{ width: isMobile ? '25%' : '20%' }}>
                  <div className="UI-th-content">
                    Main Category
                  </div>
                </th>
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''}`} style={{ width: isMobile ? '25%' : '25%' }}>
                  <div className="UI-th-content">
                    Category Level 2
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'category' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('category')}
                  style={{ cursor: 'pointer', width: isMobile ? '35%' : '45%' }}
                >
                  <div className="UI-th-content">
                    Category Level 3
                    <SortIndicator columnKey="category" />
                  </div>
                </th>
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`} style={{ width: isMobile ? '15%' : '10%' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((category) => (
                <tr
                  key={category.id}
                  className={`UI-table-row ${hoveredRow === category.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(category.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* Main Category */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === category.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: isMobile ? '25%' : '20%' }}
                  >
                    {mcategory}
                  </td>

                  {/* Category Level 2 */}
                  <td
                    className={`UI-td ${hoveredRow === category.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: isMobile ? '25%' : '25%' }}
                  >
                    {category2}
                  </td>

                  {/* Category Level 3 Name */}
                  <td
                    className={`UI-td ${hoveredRow === category.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: isMobile ? '35%' : '45%' }}
                  >
                    {highlightSearchText(category.category, searchTerm)}
                  </td>

                  {/* Actions Cell */}
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`} style={{ width: isMobile ? '15%' : '10%' }}>
                    <div className="UI-action-buttons" style={{ display: 'flex', justifyContent: 'center' }}>
                      {/* Delete Category */}
                      <Tooltip title="Delete Category" arrow slotProps={{
                        popper: {
                          sx: {
                            pointerEvents: 'none',
                          },
                        },
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCategory(category.id, category.category);
                          }}
                          className={`UI-action-button ${hoveredAction === `delete-${category.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                          onMouseEnter={() => setHoveredAction(`delete-${category.id}`)}
                          onMouseLeave={() => setHoveredAction(null)}
                          style={{ minWidth: '28px', minHeight: '28px' }}
                        >
                          <img
                            src={DeleteIcon}
                            alt="Delete"
                            className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${category.id}` ? 'UI-action-icon-hover' : ''}`}
                            style={{ width: "20px", height: "20px" }}
                          />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
            <Breadcrumb breadcrumbs={[{name:"Home",path:"/inventory"},{name:"Categories",path:"/category"},{name:"Category Level 2",path:"/categorylvl2"},{name:"Category Level 3",path:null}]} back={"/categorylvl2"}/>

              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    {mcategory} → {category2} → Category Level 3
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage level 3 category records
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* New Category Level 3 button */}
                    {(appmasterid === "2" || appmasterid === "3") && (
                      <Link
                        to="/newcategorylvl3"
                        state={{
                          id: -1,
                          name: "New Category Level3",
                          categorylvl2: category2,
                          catidlvl2: category2id,
                          mcategory: mcategory,
                          mcatid: mcatid,
                          categoryType: categoryType
                        }}
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New Category Level 3"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="UI-loading-container">
                <div className="text-center">{spin}</div>
              </div>
            ) : (
              renderTable()
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Categorylvl3;